// covered-by: frontends/carrie/src/ask-v2/home/__tests__/mentionTasks.test.ts
import { type Notification } from "@mg/schemas/src/commons/user";

import {
  isMentionNotification,
  resolveMentionNotificationTarget,
} from "../../utils/askPunttMentionNotifications";

export type AskV2MentionTask = {
  id: string;
  chatId: string;
  projectId: string | null;
  projectChatKind?: "shared" | "personal" | null;
  /** christo's copy, e.g. "Ada mentioned you in Launch."; null when absent. */
  title: string | null;
  /** ISO timestamp; empty when the stored value does not parse. */
  createdAt: string;
};

export type MentionTaskChatLookup = {
  projectId?: string | null;
  projectChatKind?: "shared" | "personal" | null;
};

export function enrichTaskFromChatLookup(
  task: AskV2MentionTask,
  chat: MentionTaskChatLookup | undefined,
): AskV2MentionTask {
  if (!chat) return task;
  return {
    ...task,
    projectId: task.projectId ?? chat.projectId ?? null,
    projectChatKind: chat.projectChatKind ?? task.projectChatKind ?? null,
  };
}

function createdAtIso(value: Notification["createdAt"]): string {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

/**
 * Unread @mentions as home tasks: one row per chat (the newest mention wins),
 * newest first. Read rows and rows with no resolvable chat are dropped, so an
 * empty result means nothing is outstanding and the home renders as before.
 */
export function mentionTasksFromNotifications(
  notifications: Notification[] | null | undefined,
  chatById?: ReadonlyMap<string, MentionTaskChatLookup>,
): AskV2MentionTask[] {
  if (!notifications?.length) return [];
  const byChat = new Map<string, AskV2MentionTask>();
  for (const notification of notifications) {
    if (!isMentionNotification(notification) || notification.isRead === true) {
      continue;
    }
    const target = resolveMentionNotificationTarget(notification);
    if (!target) continue;
    const createdAt = createdAtIso(notification.createdAt);
    const task: AskV2MentionTask = enrichTaskFromChatLookup(
      {
        id: `${target.chatId}:${createdAt}`,
        chatId: target.chatId,
        projectId: target.projectId ?? null,
        projectChatKind: null,
        title: notification.message?.trim() || null,
        createdAt,
      },
      chatById?.get(target.chatId),
    );
    const existing = byChat.get(target.chatId);
    if (!existing || existing.createdAt < createdAt) {
      byChat.set(target.chatId, task);
    }
  }
  return Array.from(byChat.values()).sort((a, b) =>
    a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0,
  );
}
